import { defineStore } from 'pinia';
import { ref } from 'vue';
import { apolloClient } from '@/lib/apollo';
import {
  MARK_NOTIFICATIONS_READ_MUTATION,
  MY_UNREAD_COUNT_UPDATED_SUBSCRIPTION,
  MY_CONVERSATIONS_QUERY,
  MY_NOTIFICATIONS_QUERY,
  MY_UNREAD_COUNT_QUERY,
} from '@/graphql/messaging';
import type {
  AppNotification,
  AppNotificationConnection,
  Conversation,
  ConversationConnection,
  QueryMyConversationsArgs,
  QueryMyNotificationsArgs,
  MutationMarkNotificationsReadArgs,
  UnreadCountSummary,
} from '@/types/graphql';

export type InboxSegment = 'chats' | 'notifications';
export type ConversationSummary = Pick<
  Conversation,
  'id' | 'unread' | 'lastMessageAt' | 'listingPost' | 'owner' | 'guest' | 'lastMessage'
>;
export type NotificationSummary = Pick<
  AppNotification,
  'id' | 'type' | 'title' | 'bodyPreview' | 'readAt' | 'created_at' | 'conversation' | 'message' | 'data'
>;
type SubscriptionHandle = { unsubscribe: () => void };
type MyNotificationsQueryVariables = Pick<QueryMyNotificationsArgs, 'first' | 'page'> & {
  unreadOnly?: QueryMyNotificationsArgs['unread_only'];
};

export const useInboxStore = defineStore('inbox', () => {
  const segment = ref<InboxSegment>('chats');
  const conversations = ref<ConversationSummary[]>([]);
  const notifications = ref<NotificationSummary[]>([]);
  const unreadSummary = ref<UnreadCountSummary | null>(null);
  const isLoadingConversations = ref(false);
  const isLoadingNotifications = ref(false);
  const isLoadingUnreadSummary = ref(false);
  const isUnreadRealtimeConnected = ref(false);
  const error = ref<string | null>(null);
  let pollTimer: number | null = null;
  let unreadSubscription: SubscriptionHandle | null = null;

  function setSegment(value: InboxSegment) {
    segment.value = value;
  }

  function setConversations(items: ConversationSummary[]) {
    conversations.value = items;
  }

  function setNotifications(items: NotificationSummary[]) {
    notifications.value = items;
  }

  function setUnreadSummary(summary: UnreadCountSummary | null) {
    unreadSummary.value = summary;
  }

  function setError(message: string | null) {
    error.value = message;
  }

  function setLoadingConversations(value: boolean) {
    isLoadingConversations.value = value;
  }

  function setLoadingNotifications(value: boolean) {
    isLoadingNotifications.value = value;
  }

  function setLoadingUnreadSummary(value: boolean) {
    isLoadingUnreadSummary.value = value;
  }

  function mapMyNotificationsArgsToVariables(options: QueryMyNotificationsArgs): MyNotificationsQueryVariables {
    return {
      first: options.first ?? 20,
      page: options.page,
      unreadOnly: options.unread_only ?? false,
    };
  }

  async function fetchConversations(options: QueryMyConversationsArgs = {}) {
    setLoadingConversations(true);
    setError(null);

    try {
      const variables: QueryMyConversationsArgs = {
        first: options.first ?? 20,
        page: options.page,
      };
      const { data } = await apolloClient.query<{ myConversations: ConversationConnection }>({
        query: MY_CONVERSATIONS_QUERY,
        variables,
        fetchPolicy: 'network-only',
      });

      const nodes = data.myConversations?.edges?.map((edge) => edge.node) ?? [];
      setConversations(nodes);
      return data.myConversations;
    } catch (error: any) {
      setError(error?.message ?? 'Unable to load conversations.');
      throw error;
    } finally {
      setLoadingConversations(false);
    }
  }

  async function fetchNotifications(options: QueryMyNotificationsArgs = {}) {
    setLoadingNotifications(true);
    setError(null);

    try {
      const variables = mapMyNotificationsArgsToVariables(options);
      const { data } = await apolloClient.query<{ myNotifications: AppNotificationConnection }>({
        query: MY_NOTIFICATIONS_QUERY,
        variables,
        fetchPolicy: 'network-only',
      });

      const nodes = data.myNotifications?.edges?.map((edge) => edge.node) ?? [];
      setNotifications(nodes);
      return data.myNotifications;
    } catch (error: any) {
      setError(error?.message ?? 'Unable to load notifications.');
      throw error;
    } finally {
      setLoadingNotifications(false);
    }
  }

  async function fetchUnreadSummary() {
    setLoadingUnreadSummary(true);
    setError(null);

    try {
      const { data } = await apolloClient.query<{ myUnreadCount: UnreadCountSummary }>({
        query: MY_UNREAD_COUNT_QUERY,
        fetchPolicy: 'network-only',
      });

      setUnreadSummary(data.myUnreadCount ?? null);
      return data.myUnreadCount ?? null;
    } catch (error: any) {
      setError(error?.message ?? 'Unable to load unread summary.');
      throw error;
    } finally {
      setLoadingUnreadSummary(false);
    }
  }

  function startPolling(callback: () => void, intervalMs: number) {
    stopPolling();
    pollTimer = window.setInterval(callback, intervalMs);
  }

  function stopPolling() {
    if (pollTimer !== null) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function subscribeToUnreadSummary() {
    if (unreadSubscription) return;

    unreadSubscription = apolloClient
      .subscribe<{ myUnreadCountUpdated: UnreadCountSummary }>({
        query: MY_UNREAD_COUNT_UPDATED_SUBSCRIPTION,
      })
      .subscribe({
        next: ({ data }) => {
          if (data?.myUnreadCountUpdated) {
            setUnreadSummary(data.myUnreadCountUpdated);
            isUnreadRealtimeConnected.value = true;
          }
        },
        error: () => {
          isUnreadRealtimeConnected.value = false;
          void fetchUnreadSummary();
        },
      });
  }

  function unsubscribeFromUnreadSummary() {
    unreadSubscription?.unsubscribe();
    unreadSubscription = null;
    isUnreadRealtimeConnected.value = false;
  }

  async function markNotificationsRead(options: MutationMarkNotificationsReadArgs) {
    try {
      const variables: MutationMarkNotificationsReadArgs = {
        ids: options.ids,
        all: options.all ?? false,
      };
      const { data } = await apolloClient.mutate<{ markNotificationsRead: boolean }>({
        mutation: MARK_NOTIFICATIONS_READ_MUTATION,
        variables,
      });

      return data?.markNotificationsRead ?? false;
    } catch (error: any) {
      setError(error?.message ?? 'Unable to mark notifications read.');
      throw error;
    }
  }

  function reset() {
    segment.value = 'chats';
    conversations.value = [];
    notifications.value = [];
    unreadSummary.value = null;
    isLoadingConversations.value = false;
    isLoadingNotifications.value = false;
    isLoadingUnreadSummary.value = false;
    isUnreadRealtimeConnected.value = false;
    error.value = null;
    stopPolling();
    unsubscribeFromUnreadSummary();
  }

  return {
    segment,
    conversations,
    notifications,
    unreadSummary,
    isLoadingConversations,
    isLoadingNotifications,
    isLoadingUnreadSummary,
    isUnreadRealtimeConnected,
    error,
    setSegment,
    setConversations,
    setNotifications,
    setUnreadSummary,
    setError,
    setLoadingConversations,
    setLoadingNotifications,
    setLoadingUnreadSummary,
    fetchConversations,
    fetchNotifications,
    fetchUnreadSummary,
    markNotificationsRead,
    startPolling,
    stopPolling,
    subscribeToUnreadSummary,
    unsubscribeFromUnreadSummary,
    reset,
  };
});
